import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { addDocument, getDocuments } from '../services/database';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency, formatInputNumber, parseFormattedNumber } from '../utils/formatters';

const CATEGORY_COLORS = {
  general:     { from: '#94A3B8', to: '#64748B' },
  rent:        { from: '#818CF8', to: '#6366F1' },
  utilities:   { from: '#F59E0B', to: '#D97706' },
  salaries:    { from: '#10B981', to: '#059669' },
  supplies:    { from: '#06B6D4', to: '#0891B2' },
  maintenance: { from: '#F97316', to: '#EA580C' },
  other:       { from: '#A78BFA', to: '#8B5CF6' }
};

const getCat = c => CATEGORY_COLORS[c] || CATEGORY_COLORS.general;

const PageContainer = styled.div`
  padding: 24px 20px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 14px 12px;
  }
`;

const PageHeader = styled.div`
  position: relative;
  padding: 22px 24px;
  margin-bottom: 24px;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.06) 0%, rgba(249, 115, 22, 0.08) 100%);
  border: 1px solid rgba(249, 115, 22, 0.18);
  border-radius: 20px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 6px 20px -8px rgba(249, 115, 22, 0.15);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, rgba(249, 115, 22, 0.2) 0%, transparent 65%);
    pointer-events: none;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 16px 16px;
    margin-bottom: 16px;
    border-radius: 16px;
  }
`;

const PageTitle = styled.h1`
  position: relative;
  margin: 0;
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -1px;
  background: linear-gradient(135deg, #B91C1C 0%, #F97316 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 1.4rem;
    letter-spacing: -0.5px;
  }
`;

const PageSubtitle = styled.p`
  position: relative;
  margin: 4px 0 0;
  font-size: 13.5px;
  color: #64748b;
  font-weight: 500;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const Panel = styled.div`
  position: relative;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 8px 24px -8px rgba(46, 91, 136, 0.08);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.$accent || 'linear-gradient(90deg, #2E5B88 0%, #4FB3BF 50%, #2E5B88 100%)'};
    opacity: 0.9;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    border-radius: 16px;
  }
`;

const PanelHeader = styled.div`
  padding: 20px 22px 14px;

  h2 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.3px;
  }

  p {
    margin: 4px 0 0;
    font-size: 13px;
    color: #64748b;
    font-weight: 500;

    strong {
      color: #dc2626;
      font-weight: 700;
    }
  }
`;

const PanelBody = styled.div`
  padding: 4px 22px 22px;

  ${props => props.$scroll && `
    max-height: 70vh;
    overflow-y: auto;
    padding-right: 12px;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-thumb {
      background: rgba(148, 163, 184, 0.35);
      border-radius: 3px;
    }
  `}
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #475569;
  font-size: 12.5px;
  letter-spacing: 0.2px;
  text-transform: uppercase;
`;

const StyledSelect = styled.select`
  padding: 11px 14px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  background: #ffffff;
  font-size: 14.5px;
  color: #1e293b;
  font-weight: 500;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%232E5B88' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 18px;
  padding-right: 40px;
  transition: border-color 160ms ease, box-shadow 160ms ease;

  &:focus {
    outline: none;
    border-color: #4FB3BF;
    box-shadow: 0 0 0 3px rgba(79, 179, 191, 0.18);
  }
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
`;

const ExpenseItem = styled.div`
  position: relative;
  padding: 14px 16px 14px 22px;
  margin-bottom: 10px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04);
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 200ms ease,
    border-color 200ms ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 14px;
    bottom: 14px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: ${props => `linear-gradient(180deg, ${getCat(props.$category).from} 0%, ${getCat(props.$category).to} 100%)`};
  }

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(79, 179, 191, 0.3);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.9) inset,
      0 8px 20px -6px rgba(46, 91, 136, 0.12);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ExpenseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;

  h4 {
    margin: 0;
    font-size: 14.5px;
    font-weight: 600;
    color: #1e293b;
    letter-spacing: -0.2px;
  }
`;

const ExpenseAmount = styled.span`
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #B91C1C 0%, #EF4444 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

const ExpenseDate = styled.span`
  display: block;
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 2px;
`;

const ExpenseDescription = styled.p`
  margin: 8px 0 0;
  color: #475569;
  font-size: 13.5px;
  line-height: 1.45;
`;

const ExpenseCategory = styled.span`
  display: inline-block;
  padding: 4px 10px;
  background: ${props => `linear-gradient(135deg, ${getCat(props.$category).from}22 0%, ${getCat(props.$category).to}22 100%)`};
  color: ${props => getCat(props.$category).to};
  border: 1px solid ${props => `${getCat(props.$category).to}33`};
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  margin-top: 10px;
`;

export const Expenses = () => {
  const { userId } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: 'general',
    date: format(new Date(), 'yyyy-MM-dd'),
    notes: ''
  });

  useEffect(() => {
    loadExpenses();
  }, [userId]);

  const loadExpenses = async () => {
    if (!userId) return;
    
    try {
      const expensesData = await getDocuments(userId, 'expenses', [], 'date', 'desc');
      setExpenses(expensesData);
    } catch (error) {
      console.error('Error loading expenses:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'amount') {
      // Format the number as user types
      const formattedValue = formatInputNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description || !formData.amount) {
      alert('Por favor complete los campos obligatorios');
      return;
    }

    setLoading(true);
    
    try {
      const expense = {
        ...formData,
        amount: parseFormattedNumber(formData.amount),
        date: formData.date,
        createdAt: new Date().toISOString()
      };

      await addDocument(userId, 'expenses', expense);
      
      // Reset form
      setFormData({
        description: '',
        amount: '',
        category: 'general',
        date: format(new Date(), 'yyyy-MM-dd'),
        notes: ''
      });
      
      // Reload expenses
      await loadExpenses();
      
      alert('Gasto registrado exitosamente');
    } catch (error) {
      console.error('Error saving expense:', error);
      alert('Error al guardar el gasto');
    } finally {
      setLoading(false);
    }
  };

  const getTotalExpenses = () => {
    return expenses.reduce((sum, expense) => sum + expense.amount, 0);
  };


  const getCategoryLabel = (category) => {
    const categories = {
      general: 'General',
      rent: 'Renta',
      utilities: 'Servicios',
      salaries: 'Salarios',
      supplies: 'Insumos',
      maintenance: 'Mantenimiento',
      other: 'Otros'
    };
    return categories[category] || category;
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Control de Gastos</PageTitle>
        <PageSubtitle>Registra y lleva el historial de los egresos de tu farmacia</PageSubtitle>
      </PageHeader>

      <Grid>
        <Panel $accent="linear-gradient(90deg, #B91C1C 0%, #F97316 50%, #B91C1C 100%)">
          <PanelHeader>
            <h2>Registrar Nuevo Gasto</h2>
            <p>Llena los campos y guarda el movimiento</p>
          </PanelHeader>
          <PanelBody>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="description">Descripción *</Label>
                <Input
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Ej: Pago de luz"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="amount">Monto *</Label>
                <Input
                  id="amount"
                  name="amount"
                  type="text"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="category">Categoría</Label>
                <StyledSelect
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <option value="general">General</option>
                  <option value="rent">Renta</option>
                  <option value="utilities">Servicios</option>
                  <option value="salaries">Salarios</option>
                  <option value="supplies">Insumos</option>
                  <option value="maintenance">Mantenimiento</option>
                  <option value="other">Otros</option>
                </StyledSelect>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="date">Fecha</Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="notes">Notas adicionales</Label>
                <Input
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Detalles adicionales (opcional)"
                  as="textarea"
                  rows={3}
                />
              </FormGroup>

              <Button
                type="submit"
                disabled={loading}
                fullWidth
              >
                {loading ? 'Guardando...' : 'Registrar Gasto'}
              </Button>
            </Form>
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHeader>
            <h2>Historial de Gastos</h2>
            <p>Total acumulado: <strong>{formatCurrency(getTotalExpenses())}</strong></p>
          </PanelHeader>
          <PanelBody $scroll>
            {expenses.length === 0 ? (
              <EmptyState>No hay gastos registrados aún</EmptyState>
            ) : (
              expenses.map(expense => (
                <ExpenseItem key={expense.id} $category={expense.category}>
                  <ExpenseHeader>
                    <div>
                      <h4>{expense.description}</h4>
                      <ExpenseDate>
                        {format(new Date(expense.date), "dd 'de' MMMM, yyyy", { locale: es })}
                      </ExpenseDate>
                    </div>
                    <ExpenseAmount>{formatCurrency(expense.amount)}</ExpenseAmount>
                  </ExpenseHeader>
                  {expense.notes && (
                    <ExpenseDescription>{expense.notes}</ExpenseDescription>
                  )}
                  <ExpenseCategory $category={expense.category}>
                    {getCategoryLabel(expense.category)}
                  </ExpenseCategory>
                </ExpenseItem>
              ))
            )}
          </PanelBody>
        </Panel>
      </Grid>
    </PageContainer>
  );
};